import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import PageHero from '../components/PageHero';
import { TierBadge } from '../components/PodcasterComponents';
import { ActivityTable } from '../components/LiveActivity';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API } from '../App';

const C = {
  bg: 'var(--pd-bg)', bg2: 'var(--pd-bg2)', surface: 'var(--pd-surface)', surface2: 'var(--pd-surface2)', surface3: 'var(--pd-surface3)',
  border: 'var(--pd-border)', text: 'var(--pd-text)', text2: 'var(--pd-text2)', muted: 'var(--pd-muted)',
  purple: 'var(--pd-purple)', cyan: 'var(--pd-cyan)', amber: 'var(--pd-amber)', green: 'var(--pd-green)', red: 'var(--pd-red)',
  gold: 'var(--pd-gold)', glowP: 'var(--pd-glow-p)', glowC: 'var(--pd-glow-c)',
  glowG: 'var(--pd-glow-g)', glass: 'var(--pd-glass)',
};

const MatchRing = ({ score = 85, size = 48 }) => {
  const strokeW = size < 40 ? 2.5 : 3;
  const r = (size - strokeW * 2) / 2, circ = 2 * Math.PI * r, offset = circ - (score / 100) * circ;
  const fontSize = size < 40 ? 9 : size < 50 ? 11 : 13;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--pd-ring-bg, rgba(255,255,255,0.06))" strokeWidth={strokeW} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={score > 80 ? C.green : score > 60 ? C.cyan : C.amber}
          strokeWidth={strokeW} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-1000" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-bold" style={{ color: C.text, fontSize }}>{score}%</span>
    </div>
  );
};


const fallbackLeaderboard = [
  { rank: 1, name: 'Dennis M. Postema', podcast_name: 'Motivational and Success', role: 'host', monthly_listeners: 47000, ranking_score: 95, avatar_url: '/dennis.png', subscription_tier: 'featured' },
  { rank: 2, name: 'Carrie Casden', podcast_name: 'Mindset Matters', role: 'both', monthly_listeners: 42000, ranking_score: 100, avatar_url: '/carrie.png', subscription_tier: 'powerhouse' },
  { rank: 3, name: 'Emma Rodriguez', podcast_name: 'Crime Time', role: 'host', monthly_listeners: 38000, ranking_score: 88, avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80', subscription_tier: 'featured' },
  { rank: 4, name: 'David Chen', podcast_name: 'The Laughter Lab', role: 'guest', monthly_listeners: 35000, ranking_score: 75, avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80', subscription_tier: 'licensed' },
  { rank: 5, name: 'Dr. Lisa Park', podcast_name: 'Science Unplugged', role: 'host', monthly_listeners: 31000, ranking_score: 65, avatar_url: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80', subscription_tier: 'licensed' },
];

export const LeaderboardPage = () => {
  const { t } = useTranslation();
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbTab, setLbTab] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/leaderboard?limit=100`)
      .then(r => {
        if (r.data && r.data.length > 0) {
          setLeaderboard(r.data.filter(user => !user.is_admin && user.role !== 'admin' && user.email !== 'admin@podcasters.directory' && user.name !== 'Admin User'));
        } else {
          setLeaderboard(fallbackLeaderboard);
        }
      })
      .catch(() => setLeaderboard(fallbackLeaderboard));
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: C.bg, color: C.text }}>
      <Navbar />
      
      <main className="flex-1">
        <PageHero 
          title="Top 100 Leaderboard" 
          subtitle="Discover the most active and top-ranking podcasters and guests on our platform." 
        />
        
        {/* ═══════════ TOP 100 LEADERBOARD ═══════════ */}
        <section className="py-16 md:py-24" data-testid="leaderboard-section">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center gap-2 mb-8">
              {[['all', t('home.leaderAll') || 'All'], ['host', t('home.leaderHosts') || 'Hosts'], ['guest', t('home.leaderGuests') || 'Guests']].map(([key, label]) => (
                <button key={key} onClick={() => setLbTab(key)}
                  className="px-5 py-2 rounded-full text-sm font-medium transition-all"
                  style={{ background: lbTab === key ? C.purple : 'transparent', color: lbTab === key ? '#fff' : C.text2, border: `1px solid ${lbTab === key ? C.purple : C.border}` }}
                  data-testid={`lb-tab-${key}`}>
                  {label}
                </button>
              ))}
            </div>
            
            <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.border}`, background: 'var(--pd-card-bg)' }}>
              <div className="hidden md:grid grid-cols-[60px_1fr_100px_80px_80px_80px] gap-4 px-6 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                <span>{t('home.leaderRank') || 'Rank'}</span>
                <span>{t('home.leaderMember') || 'Member'}</span>
                <span>{t('home.leaderRole') || 'Role'}</span>
                <span>{t('home.leaderListeners') || 'Listeners'}</span>
                <span>{t('home.leaderScore') || 'Score'}</span>
                <span>{t('home.leaderMatch') || 'Match'}</span>
              </div>
              
              {(() => {
                const filtered = leaderboard.filter(l => lbTab === 'all' || (l.role && l.role.toLowerCase().includes(lbTab)));
                if (filtered.length === 0) return (
                  <div className="px-6 py-12 text-center" data-testid="lb-empty">
                    <p className="text-sm font-medium mb-1" style={{ color: C.text2 }}>{t('home.noLeaderboard') || `No ${lbTab === 'host' ? 'hosts' : 'guests'} ranked yet`}</p>
                    <p className="text-xs" style={{ color: C.muted }}>{t('home.beFirst') || 'Be the first to claim a spot!'}</p>
                  </div>
                );
                
                return filtered.map((l, i) => (
                  <React.Fragment key={l.id || i}>
                    {/* Desktop row */}
                    <div className={`hidden md:grid grid-cols-[60px_1fr_100px_80px_80px_80px] gap-4 items-center px-6 py-4 transition-colors ${i < 3 ? '' : ''}`}
                      style={{ borderBottom: `1px solid ${C.border}`, background: i < 3 ? 'var(--pd-card-bg)' : 'transparent' }} data-testid={`lb-row-${i}`}>
                      <div className="flex items-center justify-center">
                        <span className="text-sm font-bold" style={{ color: (l.rank || i + 1) <= 3 ? C.gold : C.text2 }}>#{l.rank || i + 1}</span>
                      </div>
                      <div className="flex items-center gap-3 min-w-0">
                        <img src={l.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(l.name || 'U')}&background=8B5CF6&color=fff&size=80`} alt={l.name} className="w-9 h-9 rounded-xl object-cover shrink-0" loading="lazy" />
                        <div className="min-w-0">
                          <Link to={`/profile/${l.id}`} className="text-sm font-semibold truncate hover:underline" style={{ color: C.text }}>{l.name}</Link>
                          <div className="text-[11px] truncate" style={{ color: C.text2 }}>{l.podcast_name}</div>
                        </div>
                        <TierBadge tier={l.subscription_tier || 'free'} />
                      </div>
                      <div className="text-xs capitalize" style={{ color: l.role === 'host' ? C.purple : l.role === 'guest' ? C.cyan : C.amber }}>{l.role || 'Host'}</div>
                      <div className="text-sm font-semibold" style={{ color: C.text }}>{(l.monthly_listeners || 0).toLocaleString()}</div>
                      <div className="text-sm" style={{ color: C.green }}>{l.ranking_score || 0}</div>
                      <MatchRing score={Math.min(l.ranking_score || 50, 100)} size={36} />
                    </div>
                    
                    {/* Mobile row */}
                    <div className={`md:hidden flex items-center gap-3 px-4 py-3`}
                      style={{ borderBottom: `1px solid ${C.border}`, background: i < 3 ? 'var(--pd-card-bg)' : 'transparent' }} data-testid={`lb-row-mobile-${i}`}>
                      <span className="text-sm font-bold w-8 text-center shrink-0" style={{ color: (l.rank || i + 1) <= 3 ? C.gold : C.text2 }}>#{l.rank || i + 1}</span>
                      <img src={l.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(l.name || 'U')}&background=8B5CF6&color=fff&size=80`} alt={l.name} className="w-9 h-9 rounded-xl object-cover shrink-0" loading="lazy" />
                      <div className="flex-1 min-w-0">
                        <Link to={`/profile/${l.id}`} className="text-sm font-semibold truncate hover:underline" style={{ color: C.text }}>{l.name}</Link>
                        <div className="text-[11px] truncate" style={{ color: C.text2 }}>{l.podcast_name}</div>
                      </div>
                      <MatchRing score={Math.min(l.ranking_score || 50, 100)} size={32} />
                    </div>
                  </React.Fragment>
                ));
              })()}
            </div>
          </div>
        </section>

        {/* ═══════════ LIVE ACTIVITY ═══════════ */}
        <section className="py-6 mb-16" data-testid="activity-feed-section">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card-streamflow overflow-hidden">
              <div className="flex items-center justify-between gap-2 p-5 border-b" style={{ borderColor: C.border }}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs font-semibold" style={{ color: C.text }}>{t('home.liveActivity')}</span>
                  <span className="text-[10px] ml-2 hidden sm:inline" style={{ color: C.muted }}>{t('home.recentConnections')}</span>
                </div>
                <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: C.muted }}>
                  Last 24h
                </span>
              </div>
              <ActivityTable activities={[]} />
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};
